import { forTemplate } from '@trello/i18n';
import React, { useCallback, useContext } from 'react';
import { Button } from '@trello/nachos/button';
import { SavedViewContext, SavedViewEditable } from './SavedViewContext';

import styles from './NoResults.less';
import { ActionSubjectIdType, Analytics } from '@trello/atlassian-analytics';

const format = forTemplate('multi_board_table_view', {
  returnBlankForMissingStrings: true,
});

interface NoResultsProps {
  idOrganization: string;
}

const ClearFilters: React.FunctionComponent<{
  idOrganization: string;
  savedView: SavedViewEditable;
}> = ({ idOrganization, savedView: { filters, clearNonBoardFilters } }) => {
  const sendButtonTrackEvent = useCallback(
    (buttonName: ActionSubjectIdType) => {
      Analytics.sendClickedButtonEvent({
        buttonName: buttonName,
        source: 'multiBoardTableViewScreen',
        containers: {
          organization: {
            id: idOrganization,
          },
        },
        attributes: {
          numberBoardsSelected: filters.boards.filterLength(),
          totalMemberFiltersEnabled: filters.members.filterLength(),
          totalLabelFiltersEnabled: filters.labels.filterLength(),
          totalListFiltersEnabled: filters.list.filterLength(),
          totalDateFiltersEnabled: filters.due.filterLength(),
        },
      });
    },
    [filters, idOrganization],
  );

  const onClearCardFiltersClick = useCallback(
    function onClearCardFiltersClick() {
      sendButtonTrackEvent('clearCardFiltersButton');
      clearNonBoardFilters();
    },
    [sendButtonTrackEvent, clearNonBoardFilters],
  );

  return (
    <Button
      className={styles.clearCardFilters}
      onClick={onClearCardFiltersClick}
    >
      {format('clear-card-filters')}
    </Button>
  );
};

export const NoResults: React.FunctionComponent<NoResultsProps> = ({
  idOrganization,
}) => {
  const { savedView } = useContext(SavedViewContext);
  const { filters: viewFilters } = savedView;

  const nonBoardsFiltersActive = Object.entries(viewFilters).some(
    ([name, filter]) => name !== 'boards' && !filter.isEmpty(),
  );

  return (
    <div className={styles.empty}>
      <div className={styles.message}>{format('no-results')}</div>
      {nonBoardsFiltersActive && savedView.editable && (
        <ClearFilters idOrganization={idOrganization} savedView={savedView} />
      )}
    </div>
  );
};
